import { createConsola, ConsolaInstance } from 'consola';
import { persistentStorage, PersistentStorageKeys } from '@/utils/persistentStorage';
import { downloadFile } from '@/utils/fileHelpers';

export enum LogLevel {
  ERROR = 1,
  WARN = 2,
  INFO = 3,
  DEBUG = 4,
}

/**
 * Logger mode configuration
 * - ALL: Show all logs (subject to base level and tag-specific overrides)
 * - ONLY: Show only logs from specific tags (controlled by onlyTags)
 */
export enum LoggerMode {
  ALL = 'all',
  ONLY = 'only',
}

/**
 * Logger configuration interface for better type safety
 */
interface LoggerConfig {
  baseLevel: number;
  mode: string; // Store as string in persistence, convert to enum in runtime
  onlyTags: string[];
  overrides: [string, number][];
}

/**
 * Log entry interface for recording
 */
interface LogEntry {
  timestamp: string;
  level: string;
  tag?: string;
  message: string;
  args?: unknown[];
}

/**
 * Constants for logger configuration
 */
const LOGGER_CONSTANTS = {
  UNTAGGED_LOGGER_KEY: '__untagged__',
  DEFAULT_ON_LEVEL: LogLevel.DEBUG,
  DEFAULT_OFF_LEVEL: LogLevel.ERROR,
} as const;

/**
 * Default logger configuration
 */
const DEFAULT_LOGGER_CONFIG: Readonly<Omit<LoggerConfig, 'mode'>> = {
  baseLevel: LogLevel.INFO,
  onlyTags: [],
  overrides: [],
} as const;

class LogController {
  private baseLevel: number = LogLevel.DEBUG;
  private mode: LoggerMode = LoggerMode.ALL;
  private onlyTags = new Set<string>(); // only these tags are active (if mode='only')
  private overrides = new Map<string, number>(); // tag -> level
  private all = new Set<ConsolaInstance>();
  private byTag = new Map<string, Set<ConsolaInstance>>();

  // Log recording functionality
  private isRecording: boolean = false;
  private logBuffer: LogEntry[] = [];
  private maxBufferSize: number = 10000; // Maximum number of log entries to keep

  /**
   * Registers a logger instance with the controller
   * @param tag - Optional tag to categorize the logger
   * @param inst - The consola logger instance
   */
  register(tag: string | undefined, inst: ConsolaInstance): void {
    this.all.add(inst);
    if (tag) {
      if (!this.byTag.has(tag)) this.byTag.set(tag, new Set());
      this.byTag.get(tag)!.add(inst);
    }
    this.applyToInstance(tag, inst);
  }

  /**
   * Sets the base logging level for all loggers
   * @param n - The log level to set
   */
  setBaseLevel(n: number): void {
    this.baseLevel = n;
    this.mode = LoggerMode.ALL;
    this.applyAll();
    this.persist();
  }

  /**
   * Sets a custom log level for a specific tag
   * @param tag - The tag to set level for
   * @param n - The log level or null to remove override
   */
  setTagLevel(tag: string, n: number | null): void {
    if (n == null) this.overrides.delete(tag);
    else this.overrides.set(tag, n);
    this.applyTag(tag);
    this.persist();
  }

  only(
    tags: string[],
    onLevel: number = LOGGER_CONSTANTS.DEFAULT_ON_LEVEL,
    offLevel: number = LOGGER_CONSTANTS.DEFAULT_OFF_LEVEL,
  ) {
    this.mode = LoggerMode.ONLY;
    this.onlyTags = new Set(tags);
    // distribute: specified tags get onLevel (or their override if set),
    // others get offLevel
    for (const [tag, set] of this.byTag) {
      const active = this.onlyTags.has(tag);
      const level = active ? this.overrides.get(tag) ?? onLevel : offLevel;
      for (const l of set) l.level = level;
    }
    // instances without tag
    if (!this.byTag.has(LOGGER_CONSTANTS.UNTAGGED_LOGGER_KEY)) {
      const untagged = [...this.all].filter((l) => ![...this.byTag.values()].some((s) => s.has(l)));
      for (const l of untagged) l.level = offLevel;
    }
    this.persist();
  }

  /**
   * Shows all logs (resets mode to ALL)
   */
  showAll(): void {
    this.mode = LoggerMode.ALL;
    this.applyAll();
    this.persist();
  }

  private applyAll() {
    // for LoggerMode.ALL mode: each gets override(tag) or baseLevel
    for (const [tag, set] of this.byTag) {
      const level = this.overrides.get(tag) ?? this.baseLevel;
      for (const l of set) l.level = level;
    }
    // instances without tag
    const untagged = [...this.all].filter((l) => ![...this.byTag.values()].some((s) => s.has(l)));
    for (const l of untagged) l.level = this.baseLevel;
  }

  private applyTag(tag: string) {
    const set = this.byTag.get(tag);
    if (!set) return;
    const level =
      this.mode === LoggerMode.ONLY
        ? this.onlyTags.has(tag)
          ? this.overrides.get(tag) ?? LOGGER_CONSTANTS.DEFAULT_ON_LEVEL
          : LOGGER_CONSTANTS.DEFAULT_OFF_LEVEL
        : this.overrides.get(tag) ?? this.baseLevel;
    for (const l of set) l.level = level;
  }

  private applyToInstance(tag: string | undefined, inst: ConsolaInstance) {
    if (this.mode === LoggerMode.ONLY) {
      const active = tag ? this.onlyTags.has(tag) : false;
      inst.level = active
        ? this.overrides.get(tag ?? '') ?? LOGGER_CONSTANTS.DEFAULT_ON_LEVEL
        : LOGGER_CONSTANTS.DEFAULT_OFF_LEVEL;
    } else {
      inst.level = tag ? this.overrides.get(tag) ?? this.baseLevel : this.baseLevel;
    }
  }

  /**
   * Persists logger configuration to storage
   * @throws {Error} When persistence fails
   */
  persist(): void {
    try {
      const data: LoggerConfig = {
        baseLevel: this.baseLevel,
        mode: this.mode,
        onlyTags: [...this.onlyTags],
        overrides: [...this.overrides.entries()],
      };
      persistentStorage.set(PersistentStorageKeys.LOG_CONTROLLER, data, {
        isForActiveUser: false,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to persist log controller settings: ${errorMessage}`);
    }
  }

  /**
   * Restores logger configuration from storage
   * Falls back to defaults if restoration fails
   */
  restore(): void {
    try {
      const data = persistentStorage.get(PersistentStorageKeys.LOG_CONTROLLER, {
        isForActiveUser: false,
      });
      if (!data) return;

      // Validate and restore configuration with fallbacks
      this.baseLevel = this.validateLogLevel(data.baseLevel) ?? this.baseLevel;
      this.mode = this.validateLoggerMode(data.mode) ?? this.mode;
      this.onlyTags = new Set<string>(Array.isArray(data.onlyTags) ? data.onlyTags : []);
      this.overrides = new Map<string, number>(Array.isArray(data.overrides) ? data.overrides : []);

      // Apply restored configuration to all current loggers
      if (this.mode === LoggerMode.ONLY) {
        this.only([...this.onlyTags]);
      } else {
        this.applyAll();
      }
    } catch (error) {
      // Fall back to defaults instead of throwing to prevent app crashes
      console.warn('Failed to restore log controller settings, using defaults:', error);
      this.resetToDefaults();
    }
  }

  /**
   * Validates log level value
   * @param level - Log level to validate
   * @returns Valid log level or null
   */
  private validateLogLevel(level: unknown): number | null {
    if (typeof level !== 'number' || !Object.values(LogLevel).includes(level)) {
      return null;
    }
    return level;
  }

  /**
   * Validates logger mode value
   * @param mode - Logger mode to validate
   * @returns Valid logger mode or null
   */
  private validateLoggerMode(mode: unknown): LoggerMode | null {
    if (typeof mode !== 'string' || !Object.values(LoggerMode).includes(mode as LoggerMode)) {
      return null;
    }
    return mode as LoggerMode;
  }

  /**
   * Resets logger configuration to defaults
   */
  private resetToDefaults(): void {
    this.baseLevel = DEFAULT_LOGGER_CONFIG.baseLevel;
    this.mode = LoggerMode.ALL;
    this.onlyTags.clear();
    this.overrides.clear();
    this.applyAll();
  }

  /**
   * Starts recording logs to buffer
   * Clears existing buffer when starting new recording
   */
  startRecording(): void {
    this.isRecording = true;
    this.logBuffer = [];
  }

  /**
   * Stops recording logs
   */
  stopRecording(): void {
    this.isRecording = false;
  }

  /**
   * Clears the log buffer
   */
  clearLogs(): void {
    this.logBuffer = [];
  }

  /**
   * Gets the current recording status
   * @returns true if recording is active
   */
  isRecordingActive(): boolean {
    return this.isRecording;
  }

  /**
   * Gets the number of recorded log entries
   * @returns Number of log entries in buffer
   */
  getLogCount(): number {
    return this.logBuffer.length;
  }

  /**
   * Records a log entry to the buffer if recording is active
   * @param entry - Log entry to record
   */
  recordLog(entry: LogEntry): void {
    if (!this.isRecording) return;

    // Prevent buffer overflow
    if (this.logBuffer.length >= this.maxBufferSize) {
      // Remove oldest entries (keep last maxBufferSize - 1)
      this.logBuffer = this.logBuffer.slice(-(this.maxBufferSize - 1));
    }

    this.logBuffer.push(entry);
  }

  /**
   * Saves recorded logs to a JSONL file
   * @param filename - Optional filename (default: logs_YYYY-MM-DD_HH-MM-SS.jsonl)
   * @throws {Error} When file save fails
   */
  saveLogsToFile(filename?: string): void {
    if (this.logBuffer.length === 0) {
      throw new Error('No logs to save. Start recording first.');
    }

    try {
      // Generate filename if not provided
      const finalFilename =
        filename ||
        (() => {
          const now = new Date();
          const dateStr = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
          return `logs_${dateStr}.jsonl`;
        })();

      // Format logs as JSONL
      const logText = this.formatLogsAsJSONL();

      // Create blob and download
      const blob = new Blob([logText], { type: 'application/x-ndjson;charset=utf-8' });
      downloadFile(blob, finalFilename);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to save logs to file: ${errorMessage}`);
    }
  }

  /**
   * Formats log entries as JSONL (JSON Lines) format
   * Each line is a JSON object with: ts, level, scope (optional), msg, and additional fields from args
   * @returns Formatted JSONL text
   */
  private formatLogsAsJSONL(): string {
    const lines: string[] = [];

    for (const entry of this.logBuffer) {
      try {
        // Build base log object
        const logObj: Record<string, unknown> = {
          ts: entry.timestamp,
          level: entry.level.toLowerCase(),
          msg: entry.message,
        };

        // Add scope (tag) if present
        if (entry.tag) {
          logObj.scope = entry.tag;
        }

        // Merge additional fields from args
        if (entry.args && entry.args.length > 0) {
          for (const arg of entry.args) {
            if (typeof arg === 'object' && arg !== null && !Array.isArray(arg)) {
              // If arg is an object, merge its properties into logObj
              Object.assign(logObj, arg);
            } else if (Array.isArray(arg)) {
              // Arrays are kept as-is
              logObj.args = arg;
            } else {
              // Primitive values: try to find a meaningful key or add to a generic field
              // If we don't have an 'args' array yet, create one
              if (!logObj.args) {
                logObj.args = [];
              }
              if (Array.isArray(logObj.args)) {
                logObj.args.push(arg);
              }
            }
          }
        }

        // Convert to JSON string and add to lines
        lines.push(JSON.stringify(logObj));
      } catch (error) {
        // If JSON stringify fails, create a fallback entry
        const fallbackObj: Record<string, unknown> = {
          ts: entry.timestamp,
          level: entry.level.toLowerCase(),
          msg: entry.message,
          error: 'Failed to serialize log entry',
          raw: String(error),
        };
        if (entry.tag) {
          fallbackObj.scope = entry.tag;
        }
        lines.push(JSON.stringify(fallbackObj));
      }
    }

    return lines.join('\n');
  }
}

const logController = new LogController();

/**
 * Custom reporter for recording logs
 */
const createLogRecorderReporter = (controller: LogController) => {
  return {
    log(logObj: any) {
      if (!controller.isRecordingActive()) return;

      const timestamp = new Date().toISOString();
      const level = logObj.level?.name || 'LOG';
      const tag = logObj.tag || undefined;
      const message = logObj.args?.[0] || '';
      const args = logObj.args?.slice(1) || [];

      controller.recordLog({
        timestamp,
        level: level.toUpperCase(),
        tag,
        message: String(message),
        args,
      });
    },
  };
};

// Base logger
export const logger = createConsola({
  fancy: true,
  level: LogLevel.INFO,
  formatOptions: { date: true, colors: true, compact: false },
});
logController.register(undefined, logger);

// Add custom reporter for log recording (after logger creation to preserve default reporters)
logger.addReporter(createLogRecorderReporter(logController));

// Proxy console.* → consola
logger.wrapAll();

// Creating tag loggers
/**
 * Creates a logger with a specific tag
 * @param tag - The tag to associate with the logger
 * @returns A tagged consola logger instance
 */
export const createTaggedLogger = (tag: string): ConsolaInstance => {
  const l = logger.withTag(tag);
  logController.register(tag, l);
  return l;
};

// Global utilities for DevTools
globalThis.logger = {
  logger,
  setBaseLevel: logController.setBaseLevel.bind(logController),
  setTagLevel: logController.setTagLevel.bind(logController),
  onlyLogs: logController.only.bind(logController),
  showAllLogs: logController.showAll.bind(logController),
  startRecording: logController.startRecording.bind(logController),
  stopRecording: logController.stopRecording.bind(logController),
  saveLogsToFile: logController.saveLogsToFile.bind(logController),
  clearLogs: logController.clearLogs.bind(logController),
  isRecordingActive: logController.isRecordingActive.bind(logController),
  getLogCount: logController.getLogCount.bind(logController),
};

// Restore state on startup
logController.restore();
